import { type RootContainerProps } from "@client/content/types";
import { DynamicSelect } from "@client/controls/Inputs/Select/component";
import { globals } from "@client/globals";
import { parseQueryString, replaceQueryString, resolvePageUrl } from "@client/lib/api";
import { getEventId, setCookie } from "@client/lib/cookie";
import { cultureCodeSelector, pageSelector } from "@client/selectors";
import appInfo from "@common/appInfo";
import { type ButtonsAlign, FormStyleContext, type FormStyleContextProps, type LabelVisibility } from "@common/contexts/FormStyle";
import Flag from "@common/controls/Flag/component";
import SubmitButton from "@common/controls/Form/Buttons/Submit";
import { FormButtons, FormControlled, type FormControlledRenderParams, FormField } from "@common/controls/Form/component";
import { type OptionItem } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import * as React from "react";
import { useSelector } from "react-redux";

export type MapProps = RootContainerProps & {
  buttonCaption: string;
  buttonsAlign: ButtonsAlign;
  buttonsStyle: React.CSSProperties;
  fieldStyle: React.CSSProperties;
  labelPlacement: string;
  labelStyle: React.CSSProperties;
  labelVisibility: LabelVisibility;
  portalId: string;
  primaryButtonStyle: React.CSSProperties;
};

export interface ActionProps {}

export type Props = ActionProps & MapProps;

type FormData = {
  cultureCode: string;
  currencyCode: string;
};

export default (props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const page = useSelector(pageSelector);
  const cultureCode = useSelector(cultureCodeSelector);

  const submit = React.useCallback(
    async (data: FormData) => {
      let redirectUrl = window.location.href;
      if (cultureCode !== data.cultureCode) {
        redirectUrl = await resolvePageUrl(globals.paths.apiUrl, page.id, {
          cultureCode: data.cultureCode,
          postId: page.postId ?? undefined,
          productId: page.productId ?? undefined
        });
      }

      const qs = parseQueryString(redirectUrl);
      if (appInfo.currencyCode !== data.currencyCode) {
        setCookie("icc", data.currencyCode);

        delete qs.icc;
      }

      window.location.replace(replaceQueryString(redirectUrl, qs));
    },
    [cultureCode, page]
  );

  const renderLanguageItem = React.useCallback(
    (x: OptionItem<string>) => (
      <span style={{ display: "flex", flexDirection: "row" }}>
        <Flag cultureCode={x.value} size={22} />
        &nbsp;&nbsp;{x.label}
      </span>
    ),
    []
  );

  const value = React.useMemo<FormStyleContextProps>(
    () => ({
      fieldStyle: props.fieldStyle,
      labelPosition: props.labelPlacement === "label-inside" ? "inside" : "top",
      labelVisibility: props.labelVisibility,
      labelStyle: props.labelStyle,
      primaryButtonStyle: props.primaryButtonStyle,
      buttonsAlign: props.buttonsAlign
    }),
    [props.fieldStyle, props.labelPlacement, props.labelStyle, props.primaryButtonStyle, props.buttonsAlign]
  );

  const defaultValues = React.useMemo(
    (): FormData => ({
      cultureCode,
      currencyCode: appInfo.currencyCode
    }),
    [cultureCode]
  );

  return (
    <FormStyleContext.Provider value={value}>
      <FormControlled defaultValues={defaultValues} submit={submit} toast="">
        {({ values }: FormControlledRenderParams<FormData>) => (
          <>
            <FormField
              Component={DynamicSelect}
              componentProps={{
                options: options.getGetOptions("system", "cultures", {
                  isEnabled: true,
                  portalId: props.portalId
                }),
                selectedLabel: renderLanguageItem,
                renderItemLabel: renderLanguageItem
              }}
              isRequired
              label={i18n.language}
              name="cultureCode"
              value={values.cultureCode}
            />

            {appInfo.isMultiCurrency && (
              <FormField
                Component={DynamicSelect}
                componentProps={{
                  options: options.getGetOptions("system", "currencies", {
                    isActive: true,
                    countryCode: appInfo.countryCode,
                    eventId: getEventId()
                  })
                }}
                isRequired
                label={i18n.currency}
                name="currencyCode"
                value={values.currencyCode}
              />
            )}

            <FormButtons>
              <SubmitButton>{props.buttonCaption}</SubmitButton>
            </FormButtons>
          </>
        )}
      </FormControlled>
    </FormStyleContext.Provider>
  );
};
